/* ============================================================
   GALANTTE — Animation Controller
   IntersectionObserver-based scroll animations
   No dependencies — vanilla JS
   ============================================================ */

(function () {
  'use strict';

  /* ─── Scroll Animation Observer ─── */
  const animationObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('g-visible');
          // Once revealed, stop observing (one-time animation)
          animationObserver.unobserve(entry.target);
        }
      });
    },
    {
      threshold: 0.15,
      rootMargin: '0px 0px -50px 0px',
    }
  );

  function initScrollAnimations() {
    const animatedElements = document.querySelectorAll(
      '.g-animate, .g-animate--left, .g-animate--right, .g-animate--scale, .g-animate--fade, .g-line-reveal'
    );
    animatedElements.forEach((el) => animationObserver.observe(el));
  }

  /* ─── Counter Animation ─── */
  function animateCounter(element) {
    const target = parseInt(element.getAttribute('data-target'), 10);
    const suffix = element.getAttribute('data-suffix') || '';
    const prefix = element.getAttribute('data-prefix') || '';
    const duration = parseInt(element.getAttribute('data-duration'), 10) || 2000;

    let start = 0;
    const startTime = performance.now();

    function update(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);

      element.textContent = prefix + current + suffix;

      if (progress < 1) {
        requestAnimationFrame(update);
      }
    }

    requestAnimationFrame(update);
  }

  const counterObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateCounter(entry.target);
          counterObserver.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );

  function initCounters() {
    const counters = document.querySelectorAll('.g-counter[data-target]');
    counters.forEach((counter) => counterObserver.observe(counter));
  }

  /* ─── Parallax (lightweight, requestAnimationFrame-based) ─── */
  let parallaxElements = [];
  let ticking = false;

  function updateParallax() {
    const scrollY = window.scrollY;
    const windowHeight = window.innerHeight;

    parallaxElements.forEach(({ element, speed }) => {
      const rect = element.parentElement.getBoundingClientRect();
      const elementCenter = rect.top + rect.height / 2;
      const distanceFromCenter = elementCenter - windowHeight / 2;
      const translateY = distanceFromCenter * speed;
      element.style.transform = `translateY(${translateY}px)`;
    });

    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }

  function initParallax() {
    const elements = document.querySelectorAll('.g-parallax-bg');
    parallaxElements = Array.from(elements).map((el) => ({
      element: el,
      speed: parseFloat(el.getAttribute('data-speed')) || 0.15,
    }));

    if (parallaxElements.length > 0) {
      window.addEventListener('scroll', onScroll, { passive: true });
      updateParallax();
    }
  }

  /* ─── Staggered Children Animation ─── */
  function initStaggeredAnimations() {
    const staggerContainers = document.querySelectorAll('[data-g-stagger]');
    staggerContainers.forEach((container) => {
      const children = container.children;
      const delayStep = parseFloat(container.getAttribute('data-g-stagger')) || 0.1;
      Array.from(children).forEach((child, index) => {
        child.style.transitionDelay = `${index * delayStep}s`;
      });
    });
  }

  /* ─── Smooth reveal on page load ─── */
  function initPageLoadAnimations() {
    // Add a slight delay to allow the page to render first
    requestAnimationFrame(() => {
      document.body.classList.add('g-page-loaded');
    });
  }

  /* ─── Initialize ─── */
  function init() {
    // Respect reduced motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Still mark page as loaded, but skip scroll animations
      document.body.classList.add('g-page-loaded');
      // Make all animated elements visible immediately
      document.querySelectorAll(
        '.g-animate, .g-animate--left, .g-animate--right, .g-animate--scale, .g-animate--fade'
      ).forEach((el) => el.classList.add('g-visible'));
      return;
    }

    initScrollAnimations();
    initCounters();
    initParallax();
    initStaggeredAnimations();
    initPageLoadAnimations();
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize for Shopify section rendering (Theme Editor)
  document.addEventListener('shopify:section:load', () => {
    initScrollAnimations();
    initCounters();
    initStaggeredAnimations();
  });
})();
